import { LEGAL } from './legal';
import type { TermsBlock, TermsSection } from './terms';

export type PrivacySection = TermsSection;
export type PrivacyBlock = TermsBlock;

export const PRIVACY_INTRO = [
  `This Privacy Policy explains how ${LEGAL.operatorName}, an individual in ${LEGAL.jurisdiction} doing business as ${LEGAL.operatorDba} ("I", "we", "us", or "our"), collects, uses, and shares information when you use ${LEGAL.productName} at ${LEGAL.siteUrl} (the "Service").`,
  `The Service is a personal side project, not a company. It works together with our Terms of Service. Questions: ${LEGAL.supportEmail}.`,
] as const;

export const PRIVACY_SECTIONS: PrivacySection[] = [
  {
    id: 'collect',
    title: 'Information we collect',
    blocks: [
      {
        type: 'p',
        text: 'Depending on how you use the Service, we may collect:',
      },
      {
        type: 'ul',
        items: [
          'Account information — email address, password (stored by our authentication provider in hashed form), and authentication session data.',
          'Profile and game data — display name, XP, level, coins, gems, owned cosmetics, emotes, battle-pass progress, ranked Elo and record, match history, and similar progression.',
          'Gameplay and presence data — queue status, in-match actions needed to run games, heartbeats/presence used for online status, and forfeit or disconnect outcomes.',
          'Purchase records — product purchased, amounts, timestamps, and payment-processor references (for example Stripe session or payment ids). We do not store full payment card numbers.',
          'Feedback you send — message text, type, and associated account id, subject to rate limits.',
          'Technical data — IP address, device/browser information, and logs generated by our hosting and API providers when you use the Service.',
        ],
      },
    ],
  },
  {
    id: 'use',
    title: 'How we use information',
    blocks: [
      {
        type: 'ul',
        items: [
          'Provide, operate, and improve the Service (accounts, matchmaking, games, store, leaderboards, profiles).',
          'Process purchases and deliver Virtual Items.',
          'Secure the Service, prevent fraud and abuse, and enforce our Terms.',
          'Respond to feedback and support requests.',
          'Comply with law and protect our rights.',
        ],
      },
    ],
  },
  {
    id: 'share',
    title: 'How we share information',
    blocks: [
      {
        type: 'p',
        text: 'We do not sell your personal information. We share information with:',
      },
      {
        type: 'ul',
        items: [
          'Service providers who process data for us — including Amazon Web Services (hosting, database, authentication), Stripe (payments), and Netlify or similar hosts for the website.',
          'Other players — limited public profile information (for example display name, ranked stats, match history visible on profiles) as part of the game.',
          'Authorities or others when required by law, to protect safety, or to enforce our rights.',
          'A successor if we transfer the Service in a merger, acquisition, or similar transaction, subject to appropriate safeguards.',
        ],
      },
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies and similar technologies',
    blocks: [
      {
        type: 'p',
        text: 'We and our providers use cookies and similar technologies to keep you signed in and operate the Service. Your browser may let you block or delete cookies; doing so can break sign-in or other features. We do not use advertising cookies or third-party ad networks.',
      },
    ],
  },
  {
    id: 'retention',
    title: 'Retention',
    blocks: [
      {
        type: 'p',
        text: 'We keep account, game, and purchase records for as long as needed to provide the Service, comply with legal obligations, resolve disputes, and enforce agreements. You can request account closure from profile preferences. We will then delete or anonymize personal data we no longer need, except where retention is required (for example payment and fraud records).',
      },
    ],
  },
  {
    id: 'rights',
    title: 'Your choices and rights',
    blocks: [
      {
        type: 'ul',
        items: [
          'You can update certain profile information in the Service (subject to rules such as display-name cooldowns).',
          'You can request access to or correction of personal information we hold, or ask us to close your account, from profile preferences or by emailing us.',
          'Depending on where you live, you may have additional rights (for example to object to certain processing or to lodge a complaint with a regulator). Contact us to exercise them.',
          'You can stop using the Service at any time. Sign-out ends your session on that device.',
        ],
      },
    ],
  },
  {
    id: 'children',
    title: 'Children',
    blocks: [
      {
        type: 'p',
        text: 'The Service is not directed to children under 13. You must confirm you are 13 or older to create an account. Real-money purchases require you to confirm you are 18 or older. We do not knowingly collect personal information from children under 13. If you believe a child under 13 has created an account, contact us and we will take appropriate steps to delete the information.',
      },
    ],
  },
  {
    id: 'international',
    title: 'International transfers',
    blocks: [
      {
        type: 'p',
        text: 'We and our providers may process information in the United States and other countries. Those countries may have different data-protection laws than your home country. By using the Service you understand that your information may be transferred to and processed in those locations.',
      },
    ],
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    blocks: [
      {
        type: 'p',
        text: 'We may update this Privacy Policy from time to time. The “Effective date” will change when we do. Continued use of the Service after an update means you acknowledge the revised policy.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact',
    blocks: [
      {
        type: 'p',
        text: `Privacy questions: ${LEGAL.supportEmail}. Operator: ${LEGAL.operatorName}, an individual in ${LEGAL.jurisdiction}, doing business as ${LEGAL.operatorDba}.`,
      },
    ],
  },
];
